import { normalizarOrcamento } from "./orcamentos";
import { visualizacao3dDoItem } from "./visualizacao3d";
import { arredondarHoras } from "./horas";
import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { fbCollectionPath } from "../hooks/useFirebase";

const numero = valor => Number(valor) || 0;
const mesmoItem = (a, b) => a?.nome === b?.nome && numero(a?.preco) === numero(b?.preco) && (numero(a?.quantidade) || 1) === (numero(b?.quantidade) || 1);

// Lê o orçamento novamente dentro da transação: cliques repetidos e duas
// abas abertas disputam o mesmo documento e não criam pedidos duplicados.
export async function criarPedidoDoOrcamento(uid, orcamentoId, produtos = [], clientes = []) {
  const orcRef = doc(db, `users/${uid}/orcamentos`, orcamentoId);
  const pubRef = doc(db, `users/${uid}/orcamentos_publicos`, orcamentoId);
  const pedidoRef = doc(collection(db, fbCollectionPath("pedidos")));
  return runTransaction(db, async transaction => {
    const [orcSnap, pubSnap] = await Promise.all([transaction.get(orcRef), transaction.get(pubRef)]);
    if (!orcSnap.exists()) throw new Error("Orçamento não encontrado.");
    const orcamento = normalizarOrcamento(orcamentoId, orcSnap.data());
    const itens = (orcamento.itens || []).map(it => Object.fromEntries(Object.entries(it).filter(([,v])=>v!==undefined)));
    if (!itens.length) throw new Error("Adicione um produto ao orçamento antes de criar o pedido.");
    const publico = pubSnap.exists() ? pubSnap.data() : null;
    const usados = new Set();
    const indicesPublicos = itens.map(item => {
      const j = (publico?.itens || []).findIndex((p, i) => !usados.has(i) && mesmoItem(item, p));
      if (j >= 0) usados.add(j);
      return j;
    });
    const pendentes = itens.map((item, index) => ({item, index})).filter(({item, index}) => !item.pedido_id && !publico?.pedidosRegistrados?.[indicesPublicos[index]]);
    if (!pendentes.length) return { criado:false, pedidoId:itens.find(it => it.pedido_id)?.pedido_id || null };

    const planos = pendentes.map(({item, index}) => {
      const quantidade = Number(item.quantidade ?? 1), preco = Number(item.preco ?? 0);
      if (!Number.isSafeInteger(quantidade) || quantidade < 1 || !Number.isFinite(preco) || preco < 0 || !Number.isSafeInteger(Math.round(preco * quantidade * 100))) throw new Error("Revise a quantidade e o preço dos itens do orçamento.");
      const produto = produtos.find(p => p.id === item.produto_id) || produtos.find(p => (p.nome || "").trim().toLowerCase() === (item.nome || "").trim().toLowerCase());
      const produtoRef = produto ? doc(db, fbCollectionPath("produtos"), produto.id) : doc(collection(db, fbCollectionPath("produtos")));
      return { item, index, quantidade, preco, produtoRef, existente:!!produto };
    });
    // Todas as leituras vêm antes das gravações da transação.
    await Promise.all(planos.filter(p => p.existente).map(async p => { p.existente = (await transaction.get(p.produtoRef)).exists(); }));
    const novosProdutos = [];
    const itensPedido = planos.map(({item, index, quantidade, preco, produtoRef, existente}) => {
      const lote = Math.max(1, numero(item.unidades));
      const filamentos = (item.filamentos_usados || []).map(f => ({...f, peso:numero(f.peso)/lote, custo_total:numero(f.custo_total)/lote}));
      const materiais = (item.materiais_usados || []).map(m => ({...m, qtd:numero(m.qtd)/lote}));
      const horas = arredondarHoras(numero(item.horas_impressao)/lote);
      const nome = item.nome || "Item do orçamento";
      if (!existente) {
        const dados = {
          nome, preco, custo_total:numero(item.custo_total), estoque:0, unidades:1, ativo:false,
          fotos:(item.fotos || []).map(url => ({url})), fotoBase64:item.fotos?.[0] || null,
          horas_impressao:horas, impressora_id:item.impressora_id || null,
          mao_obra_hrs:numero(item.mao_obra_hrs)/lote, mao_obra_valor_hr:numero(item.mao_obra_valor_hr),
          filamentos_usados:filamentos, materiais_usados:materiais,
          visualizacao3d:visualizacao3dDoItem(item),
          arquivos:item.arquivo ? [item.arquivo] : [], origem:"orcamento_manual", created_at:serverTimestamp(),
          ...(item.link_makerworld ? { link_makerworld:item.link_makerworld } : {}),
        };
        transaction.set(produtoRef, dados);
        novosProdutos.push({id:produtoRef.id, ...dados});
      }
      return { produto_id:produtoRef.id, nome, qtd:quantidade, valor_unit:preco, custo_unit:numero(item.custo_total), peso:filamentos.reduce((s,f)=>s+f.peso,0), horas_impressao:horas, impressora_id:item.impressora_id || null, filamentos_usados:filamentos, obs:item.observacoes || "", orcamento_item:index };
    });
    const cliente = clientes.find(c => c.id === orcamento.clienteId);
    const valor = Math.round(itensPedido.reduce((s,it)=>s+it.valor_unit*it.qtd,0)*100)/100;
    transaction.set(pedidoRef, {
      cliente:orcamento.nomeCliente || "Cliente do orçamento", cliente_id:orcamento.clienteId || null,
      cliente_celular:cliente?.celular || "", cliente_wa:!!cliente?.aceitaWhatsapp,
      produto:itensPedido.length > 1 ? `Orçamento (${itensPedido.length} itens)` : itensPedido[0].nome,
      itens:itensPedido, valor, status:"conferindo_pagamento", pagamentos:[],
      origem:"orcamento_manual", orcamentoId, data:new Date().toLocaleDateString("pt-BR"), created_at:serverTimestamp(),
    });
    const indices = new Set(planos.map(p => p.index));
    const atualizados = itens.map((it,i) => indices.has(i) ? {...it, pedido_id:pedidoRef.id, produto_id:planos.find(p=>p.index===i).produtoRef.id} : it);
    transaction.update(orcRef, {itens:atualizados});
    if (publico) {
      const registrados = {...(publico.pedidosRegistrados || {})};
      planos.forEach(p => { const i=indicesPublicos[p.index]; if (i >= 0) registrados[i]=true; });
      transaction.update(pubRef, {pedidosRegistrados:registrados});
    }
    return {criado:true, pedidoId:pedidoRef.id, quantidadeItens:itensPedido.length, valor, itens:atualizados, novosProdutos};
  });
}